// src/components/Auth/LoginForm.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../services/api';
import {
  Box,
  Button,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Alert,   
  Snackbar,
  CircularProgress
} from '@mui/material';

import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';




const LoginForm = () => {
  const navigate = useNavigate();
  
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: false
  });
   

  const [errors, setErrors] = useState({
    email: '',
    password: '',
    api: ''
  });
  
  // API state
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // We're removing the CSRF token fetch since it's causing a 500 error
  // This assumes your Laravel backend is configured to use token authentication

  const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error on field change
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = { email: '', password: '', api: '' };
    
    // Validate email
    if (!formData.email) {
      newErrors.email = 'Email is required';
      valid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
      valid = false;
    }

    // Validate password
    if (!formData.password) {
      newErrors.password = 'Password is required';
      valid = false;
    } else if (formData.password.length < 5) {
      newErrors.password = 'Password must be at least 5 characters';
      valid = false;
    }
    
    setErrors(newErrors);
    return valid;
  };

  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous API error
    setErrors(prev => ({ ...prev, api: '' }));
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
     try {
    // Make API request to Laravel authentication endpoint
    const response = await api.post('/login', formData);
    
    console.log('Login response:', response.data);
    
    // Check if the response is successful
    if (response.data.success) {
      // Get token and user data from response
      const authToken = response.data.access_token;
      const userData = response.data.data;
      
      if (userData && authToken) {
        // Store token in localStorage FIRST
        localStorage.setItem('token', authToken);
        localStorage.setItem('user', JSON.stringify(userData));
        
        const storedToken = localStorage.getItem('token');
        console.log('Token verified in storage:', storedToken === authToken);
        
        setSnackbar({
          open: true,
          message: response.data.message || 'Login successful',
          severity: 'success'
        });
        
        // Redirect after a short delay
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 1000);
      } else {
        throw new Error('Invalid response format from server');
      }
    } else {
      throw new Error(response.data.message || 'Login failed');
    }
  } catch (error) {
    console.error('Login error:', error);
    
      
      // Handle different types of errors
      let errorMessage = 'An error occurred while logging in. Please try again.';
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.status === 422) {
          // Validation errors
          const validationErrors = error.response.data.errors;
          if (validationErrors) {
            // Set field-specific errors
            const newErrors = { ...errors };
            if (validationErrors.email) {
              newErrors.email = validationErrors.email[0];
            }
            if (validationErrors.password) {
              newErrors.password = validationErrors.password[0];
            }
            setErrors(newErrors);
            errorMessage = error.response.data.message || 'Validation failed';
          }
        } else if (error.response.status === 401) {
          // Unauthorized
          errorMessage = 'Invalid email or password';
        } else if (error.response.data && error.response.data.message) {
          // Use server provided message
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = 'Server not responding. Please check your connection.';
      }
      
      // Set API error message
      setErrors(prev => ({ ...prev, api: errorMessage }));
      
      // Show error snackbar
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 8 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          borderRadius: 2,
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: 6
          }
        }}
      >
        <Box 
          sx={{ 
            bgcolor: 'primary.main', 
            py: 3, 
            px: 4, 
            color: 'white' 
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100px"
          >
            <img    
              src="/images/depedroxi_logo.png" 
              alt="DepEd Region XI Logo" 
              width="100" 
              height="100"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = 'none';
              }}
            />
          </Box>
          <Typography sx={{ mt: 1, color: 'white', textAlign: 'center' }} variant="h4" component="h1" fontWeight="500">
            CSM ADMIN
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8, mt: 1, textAlign: 'center' }}>
            Login to Admin Dashboard
          </Typography>
        </Box>
        
        <CardContent sx={{ p: 4 }}>
          {errors.api && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.api}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              margin="normal"
              id="email"
              name="email"
              label="Email address"
              variant="outlined"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
              disabled={isLoading}
              autoComplete="email"
              autoFocus
            />
            
            <TextField
              fullWidth
              margin="normal"
              id="password"
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              disabled={isLoading}
              autoComplete="current-password"
              sx={{ mb: 3 }}
            />

            {/* <FormControlLabel
              control={
                <Checkbox 
                  checked={formData.remember}
                  onChange={handleChange}
                  name="remember"
                  color="primary"
                  disabled={isLoading}
                />
              }
              label="Remember me"
              sx={{ mb: 2 }}
            /> */}
            
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              sx={{ 
                py: 1.5, 
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                },
                position: 'relative'
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <CircularProgress 
                    size={24} 
                    color="inherit" 
                    sx={{ 
                      position: 'absolute',
                      left: 'calc(50% - 12px)'
                    }} 
                  />
                  <span style={{ visibility: 'hidden' }}>Sign In</span>
                </>
              ) : 'Sign In'}
            </Button>
          </Box>
        </CardContent>
      </Paper>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LoginForm;